import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { filter, tap } from 'rxjs/operators';

import { OrganizationsService } from '@app/services/organizations.service';
import { PartnerService } from '../../../services/partner.service';

import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { IModalComponent, IModalData } from '@app/interfaces';
import { Organization } from '@app/interfaces/accounts_list';
import { IPermissionsTreeOutput } from '@modules/shared/components/onboarding-permissions-tree/onboarding-permissions-tree.component';
import { WrapToasterService } from '@app/services/wrap-toaster-service';

enum PermissionLevel {
	management = 'Management',
	readOnly = 'Read-Only',
	payerManagement = 'Payer-Management',
	custom = 'Custom',
}

export interface IGenerateLinksOptions {
	resellerId: number;
	selectedOrganization?: Organization;
}

@Component({
	selector: 'app-partner-generate-links-modal',
	templateUrl: './partner-generate-links-modal.component.html',
	styleUrls: ['./partner-generate-links-modal.component.scss'],
})
export class PartnerGenerateLinksModalComponent implements IModalComponent<IGenerateLinksOptions>, OnInit {
	@Input() data: IModalData<IGenerateLinksOptions>;

	permissionLevel = PermissionLevel;
	resellerId: number;

	form = new FormGroup({
		permissionLevel: new FormControl(),
		organization: new FormControl(),
		accounts: new FormArray([
			new FormGroup({
				name: new FormControl(),
			}),
		]),
	});

	isLoading$ = new BehaviorSubject<boolean>(false);
	organizations: Organization[];
	defaultOrg: Organization = {} as Organization;

	permissionsSubject$ = new Subject<boolean>();
	selectedStep = 0;
	permissions: any;
	featuresList: string[];

	constructor(
		private partnerService: PartnerService,
		private orgService: OrganizationsService,
		private toastService: WrapToasterService,
	) {}

	get permission(): AbstractControl {
		return this.form.get('permissionLevel');
	}

	get organization(): AbstractControl {
		return this.form.get('organization');
	}

	get accounts(): FormArray {
		return this.form.get('accounts') as FormArray;
	}

	ngOnInit() {
		this.resellerId = this.data.options.resellerId;

		this.orgService.getOrganizations().subscribe({
			next: (organizations) => {
				this.organizations = organizations;
				this.defaultOrg = organizations.find((item) => item.org_id === this.data.options.selectedOrganization.org_id);
				this.initForm();
			},
		});
		this.nameFirstAccount();
	}

	nameFirstAccount() {
		this.organization.valueChanges
			.pipe(
				filter((_) => this.accounts.value.length === 1 && this.accounts.pristine), // change name only for first & untouched account
				tap((org) => {
					this.accounts.patchValue([{ name: org.org_name }]);
				}),
			)
			.subscribe();
	}

	initForm(): void {
		this.form = new FormGroup({
			permissionLevel: new FormControl(this.permissionLevel.management),
			organization: new FormControl(this.defaultOrg, Validators.required),
			accounts: new FormArray([
				new FormGroup({
					name: new FormControl(this.defaultOrg.org_name),
				}),
			]),
		});
	}

	addAccount(): void {
		const organizationName = this.organization.value.org_name;
		const accountsLength = this.accounts.value.length;
		const accountName = organizationName
			? `${organizationName} - ${accountsLength}`
			: `${this.defaultOrg.org_name} - ${accountsLength}`;

		const control = new FormGroup({
			name: new FormControl(accountName),
		});
		this.accounts.push(control);
	}

	removeAccount(index: number): void {
		this.accounts.removeAt(index);
	}

	generateLinks() {
		this.isLoading$.next(true);
		const linksAmount: number = this.accounts.value.length;
		const organization: Organization = this.organization.value;
		const permissionsLevel: string = this.permission.value;
		const accountsNames: string[] = this.accounts.value.map((i) => i.name);
		const permissions: any = this.permissions;
		const features_list: any = this.featuresList;

		const options = {
			reseller_id: this.resellerId,
			linksAmount,
			organization,
			permissionsLevel,
			accountsNames,
			permissions,
			features_list,
		};

		this.partnerService.generateLinks(options).subscribe(
			() => {
				setTimeout(() => {
					this.toastService.showToast('success', 'Success', `CFN links successfully generated.`);
					this.closeEvent({ message: 'linksGenerated' });
				}, 500);
			},
			(_) => {
				setTimeout(() => {
					this.toastService.showToast('error', 'Failure', 'An error has occurred during CFN link generation');
					this.closeEvent({ message: 'linksGenerated' });
				}, 500);
			},
		);
	}

	stepChanged(event: StepperSelectionEvent) {
		this.selectedStep = event.selectedIndex;

		if (this.selectedStep === 1) {
			this.permissionsSubject$.next(true);
		}
	}

	collectPermissions(event: IPermissionsTreeOutput) {
		this.permissions = event.permissionsAndFeatures.permissions;
		this.featuresList = event.permissionsAndFeatures.featuresList;
	}

	closeEvent(e?: {}): void {}
}
